import React, { useEffect, useMemo, useRef, useState } from "react";
import "./form-generic.css";
import AppButton from "../button/app-button";
import FormField from "./form-field";

import {
	createInitialValues,
	getFieldsFromSections,
	getFormRangeProgress,
	getSubmitDisabled,
	normalizeSections
} from "./utils/form-generic-utils";

const FormGeneric = ({
	formId,
	formFields = [],
	onSubmit,
	message,
	errors = {},
	buttonStartIcon = null,
	buttonEndIcon = null,
	buttonFullWidth = false,
	buttonClassName = "",
	buttonWidth = "324px",
	buttonIconLayout = "inline",
	isSubmitDisabled = null,
	onFieldChange = null,
	onFieldBlur = null,
	preserveFieldsOnUpdate = [],
	preserveDisabledFieldsOnUpdate = []
}) => {
	const sections = normalizeSections(formFields);
	const fields = getFieldsFromSections(formFields);

	const initialValues = useMemo(() => {
		return createInitialValues(formFields);
	}, [formFields]);

	const initialValuesKey = JSON.stringify(initialValues);

	const [values, setValues] = useState(initialValues);
	const [disabledFields, setDisabledFields] = useState({});
	const [loading, setLoading] = useState(false);
	const isSubmittingRef = useRef(false);

	useEffect(() => {
		setValues(previousValues => {
			const nextValues = {
				...initialValues
			};

			preserveFieldsOnUpdate.forEach(fieldName => {
				if (previousValues[fieldName] !== undefined) {
					nextValues[fieldName] = previousValues[fieldName];
				}
			});

			return nextValues;
		});

		setDisabledFields(previousDisabledFields => {
			const nextDisabledFields = {};

			preserveDisabledFieldsOnUpdate.forEach(fieldName => {
				if (previousDisabledFields[fieldName] !== undefined) {
					nextDisabledFields[fieldName] = previousDisabledFields[fieldName];
				}
			});

			return nextDisabledFields;
		});
	}, [initialValuesKey]);

	const getRangeProgress = field => {
		return getFormRangeProgress({
			field,
			value: values[field.name]
		});
	};

	const handleChange = async (name, value) => {
		setValues(previous => {
			const nextValues = {
				...previous,
				[name]: value
			};

			if (typeof onFieldChange === "function") {
				onFieldChange({
					name,
					value,
					values: nextValues,
					previousValues: previous
				});
			}

			return nextValues;
		});

		const sourceField = fields.find(field => field.name === name);
		const actions = sourceField?.actions || [];

		for (const action of actions) {
			if (action.clearTargetOnChange) {
				setValues(previous => ({
					...previous,
					[action.targetField]: ""
				}));

				setDisabledFields(previous => ({
					...previous,
					[action.targetField]: false
				}));
			}

			if (
				action.event === "complete" &&
				action.isTriggered(value)
			) {
				try {
					const response = await action.execute(value);

					const succeeded = action.isSuccess
						? action.isSuccess(response)
						: Boolean(response);

					if (!succeeded) {
						setValues(previous => ({
							...previous,
							[name]: action.clearSourceOnFailure
								? ""
								: previous[name],
							[action.targetField]: ""
						}));

						setDisabledFields(previous => ({
							...previous,
							[action.targetField]: false
						}));

						continue;
					}

					const targetValue = action.getValue
						? action.getValue(response)
						: response;

					setValues(previous => ({
						...previous,
						[action.targetField]: targetValue
					}));

					if (action.disableTargetOnSuccess) {
						setDisabledFields(previous => ({
							...previous,
							[action.targetField]: true
						}));
					}
				} catch (error) {
					setValues(previous => ({
						...previous,
						[name]: action.clearSourceOnFailure
							? ""
							: previous[name],
						[action.targetField]: ""
					}));

					setDisabledFields(previous => ({
						...previous,
						[action.targetField]: false
					}));
				}
			}
		}
	};

	const handleBlur = name => {
		if (typeof onFieldBlur !== "function") {
			return;
		}

		onFieldBlur({
			name,
			value: values[name],
			values
		});
	};

	const handleSubmit = async event => {
		event.preventDefault();

		if (isSubmittingRef.current) {
			return;
		}

		isSubmittingRef.current = true;
		setLoading(true);

		try {
			await Promise.resolve(onSubmit(values));
		} finally {
			isSubmittingRef.current = false;
			setLoading(false);
		}
	};

	const shouldRenderField = field => {
		if (typeof field.visibleWhen !== "function") {
			return true;
		}

		return field.visibleWhen(values);
	};

	const renderField = field => {
		if (!shouldRenderField(field)) {
			return null;
		}

		return (
			<div
				key={field.name}
				className="form-generic__field"
				style={{
					"--field-columns": field.columns || 12,
					"--range-progress": field.type === "range"
						? getRangeProgress(field)
						: undefined
				}}
			>
				<FormField
					field={{
						...field,
						invalidItems: errors?.[`${field.name}InvalidItems`] || [],
						disabled: field.disabled === true
							? true
							: disabledFields[field.name] ?? false
					}}
					value={values[field.name]}
					onChange={handleChange}
					onBlur={handleBlur}
					error={errors?.[field.name]}
				/>
			</div>
		);
	};

	const submitDisabled = getSubmitDisabled({
		loading,
		values,
		isSubmitDisabled
	});

	return (
		<form id={formId} className="form-generic" onSubmit={handleSubmit}>
			<div className="form-generic__grid">
				{sections.map((section, sectionIndex) => {
					const SectionIcon = section.icon;
					const hasSectionHeader = section.section || SectionIcon;

					return (
						<div
							key={section.section || `section-${sectionIndex}`}
							className="form-generic__section"
						>
							{hasSectionHeader && (
								<div className="form-generic__section-title">
									{SectionIcon && (
										<span className="form-generic__section-icon">
											<SectionIcon />
										</span>
									)}

									{section.section && (
										<span>{section.section}</span>
									)}
								</div>
							)}

							{(section.fields || []).length > 0 && (
								<div className="form-generic__section-grid">
									{(section.fields || []).map(renderField)}
								</div>
							)}

							{(section.subsections || []).map((subsection, subsectionIndex) => (
								<div
									key={subsection.subsection || `subsection-${sectionIndex}-${subsectionIndex}`}
									className="form-generic__subsection"
								>
									{subsection.subsection && (
										<div className="form-generic__subsection-title">
											{subsection.subsection}
										</div>
									)}

									<div className="form-generic__section-grid">
										{(subsection.fields || []).map(renderField)}
									</div>
								</div>
							))}
						</div>
					);
				})}

				{message && (
					<div className="form-generic__button-wrapper">
						<AppButton
							type="submit"
							disabled={submitDisabled}
							startIcon={buttonStartIcon}
							endIcon={buttonEndIcon}
							fullWidth={buttonFullWidth}
							iconLayout={buttonIconLayout}
							className={`form-generic__button ${buttonClassName}`}
							style={{
								width: buttonFullWidth ? "100%" : buttonWidth
							}}
						>
							{message}
						</AppButton>
					</div>
				)}
			</div>
		</form>
	);
};

export default FormGeneric;